/**
 * External tool auth — the auth-type discriminant for CUSTOM_MANIFEST tools and
 * the credential shape each of its variants carries.
 */

import type { TokenExchangeMethod } from "../../../schemas/AgentExternalTool/auth/CustomManifest/OAuth.schema";

/**
 * All valid auth.type values for CUSTOM_MANIFEST external tools.
 * This is the inner discriminant for AgentExternalTool.auth when tool.type = CUSTOM_MANIFEST.
 * Use CustomManifestAuthTypeSchema (z.enum) for Zod validation.
 */
export const CUSTOM_MANIFEST_AUTH_TYPES = [
  "oauth",
  "service_http",
  "user_http",
  "user_oauth",
  "keys",
  "none"
] as const;

/** Derived from CUSTOM_MANIFEST_AUTH_TYPES — single source of truth */
export type AuthType = (typeof CUSTOM_MANIFEST_AUTH_TYPES)[number];

export type ExternalToolAuth =
  | {
      type: "oauth";
      grant_type: "code" | "pkce" | "client_credentials" | "refresh_token" | "none";
      client_id: string;
      client_secret: string;
      client_url: string;
      authorization_url: string;
      scope: string;
      extra_body_params?: Record<string, string>;
      /** Absent means `DEFAULT_TOKEN_EXCHANGE_METHOD`; the manifest schema allows it. */
      tokenExchangeMethod?: TokenExchangeMethod;
      custom_header_name?: string;
      custom_header_prefix?: string;
    }
  | {
      type: "service_http";
      apiKey: string;
      authorization_type: "basic" | "bearer" | "custom";
      custom_header_name?: string;
      custom_header_prefix?: string;
    }
  | {
      type: "user_http";
      authorization_type: "basic" | "bearer" | "custom";
      custom_header_name?: string;
      custom_header_prefix?: string;
    }
  | {
      type: "user_oauth";
      grant_type: "code" | "pkce" | "client_credentials" | "refresh_token";
      authorization_type: "basic" | "bearer" | "custom";
      custom_header_name?: string;
      custom_header_prefix?: string;
    }
  | {
      type: "keys";
    }
  | {
      type: "none";
    };
